/**
 * React Query hooks for LPA Factory Settings.
 *
 * Factory settings are factory-scoped (tenant_id + factory_id).
 * At most one row per factory — the hook treats null data as "use defaults".
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/core/lib/queryKeys';
import { useTenant } from '@/core/hooks/useTenant';
import { useFactory } from '@/core/hooks/useFactory';
import { useToast } from '@/shared/ui/use-toast';
import { useLanguage } from '@/core/LanguageContext';
import { fetchFactorySettings, upsertFactorySettings } from '../api/factorySettings';
import type { UpsertFactorySettingsInput, LpaFactorySettings } from '../types';

// ---------------------------------------------------------------------------
// Defaults — mirrors the DB column defaults from the migration
// ---------------------------------------------------------------------------

export const FACTORY_SETTINGS_DEFAULTS: Omit<
  LpaFactorySettings,
  'id' | 'tenant_id' | 'factory_id' | 'created_at' | 'updated_at'
> = {
  week_start_day: 1,
  eob_hour: 17,
  schedule_generation_lead_days: 7,
  reminder_hours_before: 24,
  escalation_hours_after: 48,
};

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

export function useFactorySettings() {
  const { selectedTenantId } = useTenant();
  const { selectedFactoryId } = useFactory();

  return useQuery({
    queryKey: queryKeys.modLpaFactorySettings(selectedFactoryId),
    queryFn: () => fetchFactorySettings(selectedTenantId!, selectedFactoryId!),
    enabled: !!selectedTenantId && !!selectedFactoryId,
  });
}

// ---------------------------------------------------------------------------
// Mutation
// ---------------------------------------------------------------------------

export function useUpsertFactorySettings() {
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();
  const { selectedFactoryId } = useFactory();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (
      input: Omit<UpsertFactorySettingsInput, 'tenant_id' | 'factory_id'>,
    ) =>
      upsertFactorySettings({
        ...input,
        tenant_id: selectedTenantId!,
        factory_id: selectedFactoryId!,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.modLpaFactorySettings(selectedFactoryId),
      });
      toast({
        title: t('mod_lpa.settings.saveSuccess', 'Settings saved'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('mod_lpa.settings.saveError', 'Failed to save settings'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
